import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Droplets, Plus, Minus, Coffee, Leaf, Zap, Wine, ChevronDown, TrendingUp } from 'lucide-react';
import { showNuriNotification } from '@/components/NuriNotification';
import axios from 'axios';

const API = '/api';

const QUICK_BEVERAGES = [
  { id: 'water', label: 'Water', icon: Droplets, color: '#4A90D9' },
  { id: 'coffee', label: 'Coffee', icon: Coffee, color: '#8B6F4E' },
  { id: 'tea', label: 'Tea', icon: Leaf, color: '#6B9E8A' },
  { id: 'sports', label: 'Sports', icon: Zap, color: '#E5A84B' },
];

function WaterDroplet({ fillPercent }) {
  const clampedFill = Math.min(Math.max(fillPercent, 0), 100);
  const fillY = 100 - clampedFill;

  return (
    <svg viewBox="0 0 60 80" className="w-full h-full" style={{ maxWidth: '60px', maxHeight: '80px' }}>
      <defs>
        <clipPath id="dropClip">
          <path d="M30 4 C30 4, 6 35, 6 52 C6 65, 16 76, 30 76 C44 76, 54 65, 54 52 C54 35, 30 4, 30 4 Z" />
        </clipPath>
        <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4A90D9" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#2D6BB0" stopOpacity="1" />
        </linearGradient>
      </defs>
      <path
        d="M30 4 C30 4, 6 35, 6 52 C6 65, 16 76, 30 76 C44 76, 54 65, 54 52 C54 35, 30 4, 30 4 Z"
        fill="none" stroke="rgba(74,144,217,0.25)" strokeWidth="2"
      />
      <g clipPath="url(#dropClip)">
        <rect
          x="0" y={`${fillY * 0.72 + 4}%`}
          width="60" height={`${clampedFill * 0.72 + 4}%`}
          fill="url(#waterGrad)"
          className="transition-all duration-700 ease-out"
        />
        {clampedFill > 0 && clampedFill < 100 && (
          <path
            d={`M0 ${fillY * 0.576 + 3.2} Q15 ${fillY * 0.576 - 1.6} 30 ${fillY * 0.576 + 3.2} Q45 ${fillY * 0.576 + 8} 60 ${fillY * 0.576 + 3.2} L60 80 L0 80 Z`}
            fill="url(#waterGrad)" opacity="0.5"
            className="transition-all duration-700 ease-out"
          >
            <animate attributeName="d"
              values={`M0 ${fillY * 0.576 + 3.2} Q15 ${fillY * 0.576 - 1.6} 30 ${fillY * 0.576 + 3.2} Q45 ${fillY * 0.576 + 8} 60 ${fillY * 0.576 + 3.2} L60 80 L0 80 Z;M0 ${fillY * 0.576 + 3.2} Q15 ${fillY * 0.576 + 8} 30 ${fillY * 0.576 + 3.2} Q45 ${fillY * 0.576 - 1.6} 60 ${fillY * 0.576 + 3.2} L60 80 L0 80 Z;M0 ${fillY * 0.576 + 3.2} Q15 ${fillY * 0.576 - 1.6} 30 ${fillY * 0.576 + 3.2} Q45 ${fillY * 0.576 + 8} 60 ${fillY * 0.576 + 3.2} L60 80 L0 80 Z`}
              dur="3s" repeatCount="indefinite" />
          </path>
        )}
      </g>
    </svg>
  );
}

export function WaterTracker({ compact = false }) {
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showBeverages, setShowBeverages] = useState(false);

  const goalMl = data?.contextual_target?.target_ml || user?.water_goal_ml || 2500;
  const cupsGoal = Math.round(goalMl / 250);

  const fetchWater = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/water/today`, authHeaders());
      setData(res.data);
    } catch {}
  }, [authHeaders]);

  useEffect(() => { fetchWater(); }, [fetchWater]);

  useEffect(() => {
    const handler = () => fetchWater();
    window.addEventListener('water-logged', handler);
    return () => window.removeEventListener('water-logged', handler);
  }, [fetchWater]);

  const logBeverage = async (beverage = 'water', amount = 250) => {
    setAdding(true);
    try {
      const res = await axios.post(`${API}/water/log`, { amount_ml: amount, beverage }, authHeaders());
      setData(res.data);
      const cups = Math.round(res.data.total_ml / 250);
      if (cups >= cupsGoal) showNuriNotification('Water goal reached!');
      window.dispatchEvent(new Event('water-logged'));
    } catch {
      showNuriNotification('Failed to log', 'error');
    }
    setAdding(false);
    setShowBeverages(false);
  };

  const removeCup = async () => {
    if (!data || data.total_ml <= 0) return;
    try {
      const res = await axios.post(`${API}/water/log`, { amount_ml: -250 }, authHeaders());
      setData(res.data);
    } catch {}
  };

  const currentMl = data?.total_ml || 0;
  const cups = Math.round(currentMl / 250);
  const ctxTarget = data?.contextual_target?.target_ml || goalMl;
  const fillPercent = ctxTarget > 0 ? (currentMl / ctxTarget) * 100 : 0;
  const score = data?.hydration_score || {};
  const reasons = data?.contextual_target?.reasons || [];

  if (compact) {
    return (
      <div
        data-testid="water-tracker"
        className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 p-3 flex flex-col items-center relative overflow-hidden"
      >
        <div className="absolute inset-0 flex z-10" style={{ touchAction: 'manipulation' }}>
          <button data-testid="water-tap-minus"
            onClick={e => { e.stopPropagation(); removeCup(); }}
            onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); removeCup(); }}
            className="flex-1 active:bg-[#C67A5C]/10 transition-colors rounded-l-[16px]"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', minHeight: 44 }}
            aria-label="Remove water" />
          <button data-testid="water-tap-plus"
            onClick={e => { e.stopPropagation(); logBeverage(); }}
            onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); logBeverage(); }}
            className="flex-1 active:bg-[#4A90D9]/10 transition-colors rounded-r-[16px]"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', minHeight: 44 }}
            aria-label="Add water" />
        </div>
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#C67A5C]/15 flex items-center justify-center z-0">
          <Minus className="w-4 h-4 text-[#C67A5C]/70" />
        </div>
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#4A90D9]/15 flex items-center justify-center z-0">
          <Plus className="w-4 h-4 text-[#4A90D9]/70" />
        </div>
        <div className="w-10 h-14 mb-1 pointer-events-none">
          <WaterDroplet fillPercent={fillPercent} />
        </div>
        <p className="text-lg font-bold text-[#1A3A4A] dark:text-[#F0EDE8] leading-none pointer-events-none">
          {cups}<span className="text-[10px] font-normal text-[#7A7A72] dark:text-[#A1A1AA]">/{cupsGoal}</span>
        </p>
        <p className="text-[10px] text-[#4A90D9] font-medium mt-0.5 pointer-events-none">cups</p>
        {score.score > 0 && (
          <p className="text-[9px] font-semibold mt-0.5 pointer-events-none" style={{ color: score.color }}>
            {score.score}
          </p>
        )}
        <div className="w-full h-1 rounded-full bg-[#1A3A4A]/5 dark:bg-white/5 mt-1.5 overflow-hidden pointer-events-none">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(fillPercent, 100)}%`, backgroundColor: fillPercent >= 100 ? '#6B9E8A' : '#4A90D9' }}
          />
        </div>
        <button data-testid="hydration-details-link" onClick={() => navigate('/hydration')}
          className="text-[9px] text-[#4A90D9] font-medium mt-1 z-20 relative cursor-pointer underline"
          style={{ background: 'none', border: 'none', padding: 0 }}>details</button>
      </div>
    );
  }

  return (
    <div
      data-testid="water-tracker"
      className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 p-4"
    >
      <div className="flex items-center gap-4">
        <button
          data-testid="water-add-btn"
          onClick={() => logBeverage()}
          disabled={adding}
          className="flex-shrink-0 w-16 h-20 flex items-center justify-center active:scale-95 transition-transform cursor-pointer"
        >
          <WaterDroplet fillPercent={fillPercent} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Droplets className="w-4 h-4 text-[#4A90D9]" />
            <span className="text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">Hydration</span>
            {score.score > 0 && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ background: `${score.color}15`, color: score.color }}
                data-testid="hydration-score"
              >
                {score.score} {score.label}
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">
            {currentMl}<span className="text-sm font-normal text-[#7A7A72] dark:text-[#A1A1AA]"> / {ctxTarget} ml</span>
          </p>
          <div className="h-1.5 rounded-full bg-[#1A3A4A]/5 dark:bg-white/5 mt-2 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(fillPercent, 100)}%`, backgroundColor: fillPercent >= 100 ? '#6B9E8A' : '#4A90D9' }}
            />
          </div>
          {reasons.length > 0 && (
            <p className="text-[10px] text-[#E5A84B] mt-1">
              <TrendingUp className="w-3 h-3 inline mr-0.5" />{reasons[0]}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            data-testid="water-bev-toggle"
            onClick={() => setShowBeverages(!showBeverages)}
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
            style={{ backgroundColor: 'rgba(74,144,217,0.15)' }}
          >
            <ChevronDown className={`w-5 h-5 text-[#4A90D9] transition-transform ${showBeverages ? 'rotate-180' : ''}`} />
          </button>
          <button
            data-testid="water-minus"
            onClick={removeCup}
            disabled={cups <= 0}
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90 disabled:opacity-30"
            style={{ backgroundColor: 'rgba(122,122,114,0.1)' }}
          >
            <Minus className="w-5 h-5 text-[#7A7A72]" />
          </button>
        </div>
      </div>

      {/* Beverage Type Selector */}
      {showBeverages && (
        <div className="mt-3 pt-3 border-t border-[#E0DCD4] dark:border-white/10" data-testid="beverage-picker">
          <p className="text-xs text-[#7A7A72] mb-2">Quick log (250ml):</p>
          <div className="flex gap-2 flex-wrap">
            {QUICK_BEVERAGES.map(b => {
              const Icon = b.icon;
              return (
                <button
                  key={b.id}
                  data-testid={`bev-${b.id}`}
                  onClick={() => logBeverage(b.id)}
                  disabled={adding}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all active:scale-95 border"
                  style={{ borderColor: `${b.color}25`, background: `${b.color}08` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: b.color }} />
                  <span className="text-xs font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">{b.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
